import React from 'react';
import MarkdownRenderer from './MarkdownRenderer';
import { streamChat } from '../lib/chatClient';
import { answerLocally } from '../lib/localFallback';

const GREETING =
  "Hi! I'm Monk, Shubhajeet's assistant. Ask me about his projects, experience, skills, writing, or how to get in touch.";

// Friendly quick-starts shown before the first question.
const SUGGESTIONS = [
  { label: '🚀 Projects', text: 'What projects has Shubhajeet built?' },
  { label: '💼 Experience', text: 'Tell me about his work experience.' },
  { label: '🛠️ Skills', text: 'What are his technical skills?' },
  { label: '✉️ Contact', text: 'How can I get in touch with him?' },
];

// How many prior turns of context we send with each request.
const HISTORY_TURNS = 8;

export default function TerminalChat() {
  const [messages, setMessages] = React.useState([{ role: 'bot', text: GREETING }]);
  const [query, setQuery] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [mode, setMode] = React.useState('ready'); // ready | ai | offline
  const [isDark, setIsDark] = React.useState(false);
  const chatRef = React.useRef(null);

  React.useEffect(() => {
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Replace the trailing (pending) bot bubble as tokens stream / on completion.
  const updateLastBot = (patch) =>
    setMessages((m) => {
      const copy = [...m];
      const i = copy.length - 1;
      if (i >= 0 && copy[i].role === 'bot') copy[i] = { ...copy[i], ...patch };
      return copy;
    });

  function onSubmit(e) {
    e.preventDefault();
    send(query);
  }

  async function send(text) {
    const q = (text || '').trim();
    if (!q || loading) return;

    // Build the outgoing history from real turns (skip earlier errors), then the new question.
    const priorTurns = messages
      .filter((m) => !m.isError)
      .map((m) => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.text }));
    const outgoing = [...priorTurns, { role: 'user', content: q }].slice(-HISTORY_TURNS);

    setMessages((m) => [...m, { role: 'user', text: q }, { role: 'bot', text: '', pending: true }]);
    setQuery('');
    setLoading(true);

    let streamed = '';
    try {
      const { text } = await streamChat(outgoing, {
        onToken: (chunk) => {
          streamed += chunk;
          updateLastBot({ text: streamed, pending: true });
        },
      });
      const finalText = (text || streamed).trim();
      if (!finalText) throw new Error('Empty response');
      updateLastBot({ text: finalText, pending: false });
      setMode('ai');
    } catch (err) {
      // Backend unreachable — answer from local profile data so the visitor isn't stuck.
      const fallback = answerLocally(q);
      updateLastBot({ text: fallback, pending: false, isError: false });
      setMode('offline');
    } finally {
      setLoading(false);
    }
  }

  const modeLabel = mode === 'ai' ? 'AI' : mode === 'offline' ? 'Offline' : 'Ready';

  return (
    <div
      className={`terminal ${isDark ? 'terminal-dark' : 'terminal-light'}`}
      role="region"
      aria-label="Chat with Shubhajeet's assistant"
      style={{ marginTop: 12 }}
    >
      <div className="terminal-top" style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="dot red" />
          <span className="dot yellow" />
          <span className="dot green" />
        </div>
        <div className="tabs" aria-label="chat header" style={{ alignItems: 'center', display: 'flex', gap: 12 }}>
          <span className="tab active" aria-current>Assistant</span>
          <span className="mode-badge" title="Response source">{modeLabel}</span>
          <label className="theme-switch">
            <input
              type="checkbox"
              checked={isDark}
              onChange={() => setIsDark((v) => !v)}
              aria-label={`Switch to ${isDark ? 'light' : 'dark'} theme`}
            />
            <span className="theme-slider"></span>
          </label>
        </div>
      </div>

      <div className="terminal-body term-body">
        <div className="term-messages" ref={chatRef} role="log" aria-live="polite" aria-busy={loading}>
          {messages.map((m, i) => (
            <div key={i} className={`term-msg ${m.role}`}>
              {m.role === 'bot' ? (
                m.pending && !m.text ? (
                  <span className="typing" aria-label="Assistant is typing">
                    <span></span><span></span><span></span>
                  </span>
                ) : (
                  <MarkdownRenderer content={m.text} />
                )
              ) : (
                <div className="term-user-text">{m.text}</div>
              )}
            </div>
          ))}
          {messages.length === 1 && !loading && (
            <div className="chat-suggestions" aria-label="Suggested questions">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  className="suggestion-chip"
                  onClick={() => send(s.text)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <form className="term-input" onSubmit={onSubmit}>
          <input
            type="text"
            placeholder="Ask about Shubhajeet's projects, experience, or skills…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Ask the assistant a question"
            disabled={loading}
          />
          <button type="submit" className="copy-btn" disabled={loading || !query.trim()}>
            {loading ? 'Sending…' : 'Ask'}
          </button>
        </form>
      </div>
    </div>
  );
}
